// hooks/useCategorySearchLogic.ts

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCategory } from "@/context";
import { filterCombinationsByColorAndSize } from "@/utils";
export const useCategorySearchLogic = ({
  slug,
  updateURL,
  syncFiltersFromURL
}) => {
  // States
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMinPrice, setSelectedMinPrice] = useState(null);
  const [selectedMaxPrice, setSelectedMaxPrice] = useState(null);
  const [showOnSaleOnly, setShowOnSaleOnly] = useState(false);
  const [selectedOption, setSelectedOption] = useState("default");
  const [initialized, setInitialized] = useState(false);

  // Applied filters
  const [appliedColorOptions, setAppliedColorOptions] = useState([]);
  const [appliedSizeOptions, setAppliedSizeOptions] = useState([]);
  const [appliedFeatureOptions, setAppliedFeatureOptions] = useState([]);
  const [appliedMinPrice, setAppliedMinPrice] = useState(0);
  const [appliedMaxPrice, setAppliedMaxPrice] = useState(0);
  const [appliedOnSaleOnly, setAppliedOnSaleOnly] = useState(false);

  // Selected filters (UI state)
  const [selectedColorOptions, setSelectedColorOptions] = useState([]);
  const [selectedSizeOptions, setSelectedSizeOptions] = useState([]);
  const [selectedFeatureOptions, setSelectedFeatureOptions] = useState([]);

  // Filter data from category response
  const [filterData, setFilterData] = useState(null);
  const [sortOrders, setSortOrders] = useState(null);

  // Refs
  const searchTimeoutRef = useRef(null);
  const lastSearchRef = useRef("");
  const isSearchingRef = useRef(false);

  // Category context
  const {
    state: {
      category,
      loadingCategory,
      loadingMore,
      hasMorePages,
      currentPage: contextCurrentPage
    },
    getCategoryData,
    loadMoreProducts
  } = useCategory();

  // Sync current page with context
  useEffect(() => {
    if (contextCurrentPage !== currentPage) {
      setCurrentPage(contextCurrentPage);
    }
  }, [contextCurrentPage]);

  // Create search key
  const createSearchKey = useCallback((slug_0, colors, sizes, features, sort, minPrice, maxPrice, onSaleOnly) => {
    return `${slug_0}-${colors.map(c => c.id).join(",")}-${sizes.map(s => s.id).join(",")}-${features.map(f => f.id).join(",")}-${sort}-${minPrice}-${maxPrice}-${onSaleOnly}`;
  }, []);

  // Core search function
  const performSearch = useCallback(async (colors_0, sizes_0, features_0, minPrice_0, maxPrice_0, onSaleOnly_0, immediate = false) => {
    if (!slug || !initialized || isSearchingRef.current) return;
    if (filterData && minPrice_0 === null && maxPrice_0 === null && (filterData.minPrice > 0 || filterData.maxPrice > 0)) {
      console.log("Waiting for filters to be properly initialized...");
      return;
    }
    const searchKey = createSearchKey(slug, colors_0, sizes_0, features_0, selectedOption, minPrice_0 ?? 0, maxPrice_0 ?? 0, onSaleOnly_0);

    // Skip duplicate searches
    if (lastSearchRef.current === searchKey && !immediate) {
      console.log("Skipping duplicate search:", searchKey);
      return;
    }

    // Cancel previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    const executeSearch = async () => {
      if (isSearchingRef.current) return;
      console.log("Performing category search with key:", searchKey);
      isSearchingRef.current = true;
      lastSearchRef.current = searchKey;
      try {
        const defaultMinPrice = filterData ? Math.floor(Number(filterData.minPrice || 0)) : 0;
        const defaultMaxPrice = filterData ? Math.floor(Number(filterData.maxPrice || 0)) : 0;
        const searchFilters = {
          colors: colors_0.map(option => option.id),
          sizes: sizes_0.map(option_0 => option_0.id),
          features: features_0.map(option_1 => option_1.id),
          minPrice: minPrice_0 !== null && minPrice_0 !== defaultMinPrice ? minPrice_0 : null,
          maxPrice: maxPrice_0 !== null && maxPrice_0 !== defaultMaxPrice ? maxPrice_0 : null,
          onSaleOnly: onSaleOnly_0,
          sort: selectedOption
        };
        await getCategoryData(slug, 1, searchFilters);

        // Update applied filters after successful response
        setAppliedColorOptions(colors_0);
        setAppliedSizeOptions(sizes_0);
        setAppliedFeatureOptions(features_0);
        setAppliedMinPrice(minPrice_0 ?? defaultMinPrice);
        setAppliedMaxPrice(maxPrice_0 ?? defaultMaxPrice);
        setAppliedOnSaleOnly(onSaleOnly_0);

        // Update URL after success
        updateURL(colors_0, sizes_0, features_0, minPrice_0 ?? defaultMinPrice, maxPrice_0 ?? defaultMaxPrice, onSaleOnly_0, selectedOption, filterData);
      } catch (error) {
        console.error("Category search error:", error);
      } finally {
        isSearchingRef.current = false;
      }
    };
    if (immediate) {
      await executeSearch();
    } else {
      searchTimeoutRef.current = setTimeout(executeSearch, 300);
    }
  }, [slug, selectedOption, getCategoryData, createSearchKey, updateURL, initialized, filterData]);

  // Stable function to perform search with current applied filters
  const performSearchWithCurrentFilters = useCallback(async () => {
    if (!initialized || isSearchingRef.current) return;
    await performSearch(appliedColorOptions, appliedSizeOptions, appliedFeatureOptions, appliedMinPrice, appliedMaxPrice, appliedOnSaleOnly, true);
  }, [performSearch, initialized, appliedColorOptions, appliedSizeOptions, appliedFeatureOptions, appliedMinPrice, appliedMaxPrice, appliedOnSaleOnly]);

  // Handle apply filters
  const handleApplyFilters = useCallback((colors_1, sizes_1, features_1, minPrice_1, maxPrice_1, onSaleOnly_1) => {
    setCurrentPage(1);
    performSearch(colors_1, sizes_1, features_1, minPrice_1 ?? 0, maxPrice_1 ?? 0, onSaleOnly_1, true);
  }, [performSearch]);

  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (hasMorePages && !loadingMore && slug) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadMoreProducts(slug, nextPage, {
        colors: appliedColorOptions.map(option_2 => option_2.id),
        sizes: appliedSizeOptions.map(option_3 => option_3.id),
        features: appliedFeatureOptions.map(option_4 => option_4.id),
        minPrice: appliedMinPrice,
        maxPrice: appliedMaxPrice,
        onSaleOnly: appliedOnSaleOnly,
        sort: selectedOption
      });
    }
  }, [hasMorePages, loadingMore, slug, currentPage, loadMoreProducts]);

  // Initialize from URL - UPROSZCZONE, używa syncFiltersFromURL
  const initializeFromURL = useCallback(async (filterData_0, urlFilters) => {
    try {
      console.log("=== INITIALIZE FROM URL ===");
      console.log("filterData received:", filterData_0);
      console.log("urlFilters received:", urlFilters);

      // UŻYJ funkcji synchronizacji z useCategoryParams
      syncFiltersFromURL(filterData_0);

      // Ustaw search key dla uniknięcia duplikatów
      const {
        colors: colors_2,
        sizes: sizes_2,
        features: features_2,
        minPrice: minPriceFromUrl,
        maxPrice: maxPriceFromUrl,
        onSaleOnly: onSaleParam
      } = urlFilters;
      const hasUrlFilters = colors_2.length > 0 || sizes_2.length > 0 || features_2.length > 0 || minPriceFromUrl !== Math.floor(Number(filterData_0.minPrice || 0)) || maxPriceFromUrl !== Math.floor(Number(filterData_0.maxPrice || 0)) || onSaleParam;

      // Zapisz aktualny klucz wyszukiwania
      const currentSearchKey = createSearchKey(slug || "", hasUrlFilters ? colors_2 : [], hasUrlFilters ? sizes_2 : [], hasUrlFilters ? features_2 : [], selectedOption, hasUrlFilters ? minPriceFromUrl : Math.floor(Number(filterData_0.minPrice || 0)), hasUrlFilters ? maxPriceFromUrl : Math.floor(Number(filterData_0.maxPrice || 0)), hasUrlFilters ? onSaleParam : false);
      lastSearchRef.current = currentSearchKey;
      console.log("=== END INITIALIZE FROM URL ===");
    } catch (error_0) {
      console.error("Category initialization error:", error_0);
    }
  }, [slug, selectedOption, createSearchKey, syncFiltersFromURL]);

  // Set filter data when category is loaded
  useEffect(() => {
    if (category?.filterData && !filterData) {
      setFilterData(category.filterData);
    }
    if (category?.sortOrders && !sortOrders) {
      setSortOrders(category.sortOrders);
    }
  }, [category, filterData, sortOrders]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      isSearchingRef.current = false;
    };
  }, []);

  // Computed values
  const filteredResults = useMemo(() => {
    if (!category?.products) return [];
    const selectedColors = appliedColorOptions.map(option_5 => option_5.label);
    const selectedSizes = appliedSizeOptions.map(option_6 => option_6.label);
    const selectedFeatures = appliedFeatureOptions.map(option_7 => option_7.label);
    return filterCombinationsByColorAndSize(category.products, selectedColors, selectedSizes, selectedFeatures);
  }, [category?.products, appliedColorOptions, appliedSizeOptions, appliedFeatureOptions]);
  return {
    // States
    currentPage,
    setCurrentPage,
    selectedMinPrice,
    setSelectedMinPrice,
    selectedMaxPrice,
    setSelectedMaxPrice,
    showOnSaleOnly,
    setShowOnSaleOnly,
    selectedOption,
    setSelectedOption,
    initialized,
    setInitialized,
    // Applied filters
    appliedColorOptions,
    appliedSizeOptions,
    appliedFeatureOptions,
    appliedMinPrice,
    appliedMaxPrice,
    appliedOnSaleOnly,
    // Selected filters
    selectedColorOptions,
    selectedSizeOptions,
    selectedFeatureOptions,
    setSelectedColorOptions,
    setSelectedSizeOptions,
    setSelectedFeatureOptions,
    // Category state
    category,
    loadingCategory,
    loadingMore,
    hasMorePages,
    filterData,
    sortOrders,
    // Functions
    handleApplyFilters,
    handleLoadMore,
    initializeFromURL,
    performSearchWithCurrentFilters,
    // Computed
    filteredResults
  };
};